import { CSSProperties } from 'react';
import Label from './Label';
import LabelNoHover from './LabelNoHover';
import { ILabel } from '../constants/interfaces';

export interface IFieldLabelProps {
  /** The label row as configured in the database. */
  field: ILabel;
  /** Ties the label to its control for screen readers. */
  htmlFor?: string;
  /** Callers that render their own label, or lay the field out without one. */
  isHidden?: boolean;
  width?: string | number;
  align?: 'left' | 'center';
  /**
   * Renders the non-interactive variant. Only MultiSelectField sets this today —
   * see the note below.
   */
  noHover?: boolean;
  noBorder?: boolean;
  style?: CSSProperties;
}

/**
 * The single place a field's label is derived from its database metadata.
 *
 * `text` and `isMandatory` come straight off the configured label row, so what the
 * user sees follows the configuration rather than each field type deciding for
 * itself. Six field components previously repeated this block verbatim, which is
 * how MultiSelectField came to render a different label component from every other
 * type for identical metadata.
 *
 * That difference is preserved here behind `noHover` rather than silently changed,
 * since it alters what the user sees. Drop the prop from MultiSelectField to bring
 * it in line with the rest.
 */
function FieldLabel({
  field,
  htmlFor,
  isHidden = false,
  width,
  align,
  noHover = false,
  noBorder,
  style,
}: IFieldLabelProps) {
  if (isHidden || !field) {
    return null;
  }

  if (noHover) {
    return (
      <LabelNoHover
        text={field.text}
        required={field.isMandatory}
        htmlFor={htmlFor}
        width={width}
        noBorder={noBorder}
        style={style}
      />
    );
  }

  return (
    <Label
      text={field.text}
      required={field.isMandatory}
      htmlFor={htmlFor}
      width={width}
      align={align}
    />
  );
}

export default FieldLabel;
